import { useMemo, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { NAV_MODULES, NAV_GROUPS } from "@/config/nav";
import { cn } from "@/lib/utils";
import { Search, ChevronsLeft, Lock, TrendingUp } from "lucide-react";
import { Input } from "@/components/ui/input";

export function Sidebar({ collapsed, setCollapsed, mobile = false, onNavigate }) {
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return NAV_MODULES;
    return NAV_MODULES.filter((m) => m.label.toLowerCase().includes(q));
  }, [query]);

  return (
    <aside
      data-testid={mobile ? "mobile-sidebar" : "sidebar"}
      className={cn(
        "flex flex-col bg-[var(--surface)]",
        mobile ? "h-full w-full" : "h-screen sticky top-0 border-r border-[var(--border-subtle)] transition-[width] duration-300",
        !mobile && (collapsed ? "w-16" : "w-64")
      )}
    >
      <div className="h-14 flex items-center gap-2 px-4 border-b border-[var(--border-subtle)] shrink-0">
        <button
          data-testid="logo-home-btn"
          onClick={() => { navigate("/app/dashboard"); onNavigate?.(); }}
          className="flex items-center gap-2 overflow-hidden"
        >
          <div className="w-8 h-8 rounded-md bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shrink-0">
            <TrendingUp className="w-5 h-5 text-white" />
          </div>
          {!collapsed && (
            <div className="flex items-baseline gap-1">
              <span className="font-display text-lg font-bold text-white tracking-tight">Tradify</span>
              <span className="text-[9px] font-mono text-blue-400 border border-blue-500/30 rounded px-1">P1</span>
            </div>
          )}
        </button>
      </div>

      {!collapsed && (
        <div className="p-3 border-b border-[var(--border-subtle)]">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input
              data-testid="sidebar-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter modules..."
              className={cn("h-8 pl-8 bg-[var(--bg-canvas)] border-[var(--border-subtle)] text-slate-200", mobile ? "text-base" : "text-xs")}
            />
          </div>
        </div>
      )}

      <nav className="flex-1 overflow-y-auto py-2 px-2">
        {NAV_GROUPS.map((group) => {
          const items = filtered.filter((m) => m.group === group);
          if (!items.length) return null;
          return (
            <div key={group} className="mb-3">
              {!collapsed && (
                <div className="px-2 mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-600">
                  {group}
                </div>
              )}
              <div className="space-y-0.5">
                {items.map((m) => {
                  const Icon = m.icon;
                  return (
                    <NavLink
                      key={m.key}
                      to={m.path}
                      data-testid={mobile ? `mobile-nav-${m.key}` : `nav-${m.key}`}
                      onClick={onNavigate}
                      title={collapsed ? m.label : undefined}
                      className={({ isActive }) =>
                        cn(
                          "group flex items-center gap-2.5 rounded-md px-2.5 py-2 text-xs font-medium transition-colors",
                          isActive
                            ? "bg-blue-500/15 text-blue-300 border border-blue-500/20"
                            : "text-slate-400 hover:text-slate-100 hover:bg-[var(--card-hover)] border border-transparent"
                        )
                      }
                    >
                      <Icon className="w-4 h-4 shrink-0" />
                      {!collapsed && <span className="truncate flex-1">{m.label}</span>}
                      {!collapsed && !m.live && (
                        <Lock className="w-3 h-3 text-slate-600 shrink-0" />
                      )}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      {!mobile && <button
        data-testid="sidebar-collapse-btn"
        onClick={() => setCollapsed(!collapsed)}
        className="h-10 border-t border-[var(--border-subtle)] flex items-center justify-center text-slate-500 hover:text-slate-200 hover:bg-[var(--card-hover)] transition-colors"
      >
        <ChevronsLeft className={cn("w-4 h-4 transition-transform", collapsed && "rotate-180")} />
      </button>}
    </aside>
  );
}
